import { useCallback, useEffect, useRef, useState } from 'react';

interface UseLongPressOptions {
  threshold?: number;
  moveThreshold?: number;
  onLongPress: (event: React.TouchEvent | React.MouseEvent) => void;
  onPressStart?: (event: React.TouchEvent | React.MouseEvent) => void;
  onPressEnd?: (event: React.TouchEvent | React.MouseEvent) => void;
}

interface UseLongPressResult {
  isPressing: boolean;
  handlers: {
    onMouseDown: (e: React.MouseEvent) => void;
    onMouseUp: (e: React.MouseEvent) => void;
    onMouseLeave: (e: React.MouseEvent) => void;
    onTouchStart: (e: React.TouchEvent) => void;
    onTouchEnd: (e: React.TouchEvent) => void;
    onTouchMove: (e: React.TouchEvent) => void;
    onContextMenu: (e: React.MouseEvent) => void;
  };
}

export function useLongPress(options: UseLongPressOptions): UseLongPressResult {
  const { threshold = 500, moveThreshold = 10, onLongPress, onPressStart, onPressEnd } = options;
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startPosRef = useRef<{ x: number; y: number } | null>(null);
  const isLongPressTriggeredRef = useRef(false);
  const [isPressing, setIsPressing] = useState(false);

  // Track latest callback refs to avoid stale closures
  const onLongPressRef = useRef(onLongPress);
  onLongPressRef.current = onLongPress;
  const onPressStartRef = useRef(onPressStart);
  onPressStartRef.current = onPressStart;
  const onPressEndRef = useRef(onPressEnd);
  onPressEndRef.current = onPressEnd;

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      clearTimer();
    };
  }, [clearTimer]);

  const startPress = useCallback(
    (event: React.TouchEvent | React.MouseEvent) => {
      if (!('touches' in event)) {
        event.preventDefault();
      }
      isLongPressTriggeredRef.current = false;
      setIsPressing(true);
      onPressStartRef.current?.(event);

      let clientX: number;
      let clientY: number;

      if ('touches' in event) {
        clientX = event.touches[0].clientX;
        clientY = event.touches[0].clientY;
      } else {
        clientX = event.clientX;
        clientY = event.clientY;
      }

      startPosRef.current = { x: clientX, y: clientY };

      timerRef.current = setTimeout(() => {
        isLongPressTriggeredRef.current = true;
        onLongPressRef.current(event);
        setIsPressing(false);
      }, threshold);
    },
    [threshold]
  );

  const endPress = useCallback(
    (event: React.TouchEvent | React.MouseEvent) => {
      clearTimer();
      setIsPressing(false);
      startPosRef.current = null;
      onPressEndRef.current?.(event);
    },
    [clearTimer]
  );

  const handleTouchMove = useCallback(
    (event: React.TouchEvent) => {
      if (!startPosRef.current) return;

      const touch = event.touches[0];
      const dx = Math.abs(touch.clientX - startPosRef.current.x);
      const dy = Math.abs(touch.clientY - startPosRef.current.y);

      if (dx > moveThreshold || dy > moveThreshold) {
        clearTimer();
        setIsPressing(false);
        startPosRef.current = null;
        onPressEndRef.current?.(event);
      }
    },
    [moveThreshold, clearTimer]
  );

  const handleContextMenu = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
    },
    []
  );

  return {
    isPressing,
    handlers: {
      onMouseDown: startPress,
      onMouseUp: endPress,
      onMouseLeave: endPress,
      onTouchStart: startPress,
      onTouchEnd: endPress,
      onTouchMove: handleTouchMove,
      onContextMenu: handleContextMenu,
    },
  };
}
